// Globals
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// Components
import { Form, IFormInstance, useWatchForm } from '@/components';
import { Button, DatePicker, Input, InputNumber, Select, TextEditorWithHtml } from '@/ui';
import CouponPreview from '../CouponPreview/CouponPreview';
import { StyledFormItem, StyledRow } from './CouponFormStyles';

// Helpers
import { CouponFormFields, TCreateCouponFormValues, getCouponTemplateOptions } from '../../helpers';
import { useCouponFormRules } from '../../hooks';

type TCouponFormProps = {
  initialValues?: Partial<TCreateCouponFormValues>;
  onSubmit: (values: TCreateCouponFormValues) => void;
  isLoading?: boolean;
  onReset: () => void;
  submitText?: string;
  formInstance: IFormInstance;
};

const CouponForm: FC<TCouponFormProps> = ({
  initialValues,
  isLoading,
  formInstance,
  onSubmit,
  onReset,
  submitText,
}) => {
  const { t } = useTranslation(['common']);
  const dateFrom = useWatchForm(CouponFormFields.DATE_FROM, formInstance);
  const rules = useCouponFormRules();

  const couponTemplateOptions = useMemo(() => getCouponTemplateOptions(t), [t]);

  return (
    <Form form={formInstance} onReset={onReset} onFinish={onSubmit} initialValues={initialValues}>
      <StyledFormItem
        rules={rules[CouponFormFields.CODE]}
        label={t('label_coupon_number')}
        name={CouponFormFields.CODE}
      >
        <Input placeholder={t('placeholder_coupon_number')} />
      </StyledFormItem>
      <StyledRow>
        <StyledFormItem label={t('label_coupon_start_date')} name={CouponFormFields.DATE_FROM}>
          <DatePicker minDate={dayjs()} placeholder={t('placeholder_coupon_start_date')} />
        </StyledFormItem>
        <StyledFormItem label={t('label_coupon_end_date')} name={CouponFormFields.DATE_TO}>
          <DatePicker minDate={dateFrom || dayjs()} placeholder={t('placeholder_coupon_end_date')} />
        </StyledFormItem>
      </StyledRow>
      <StyledFormItem
        rules={rules[CouponFormFields.RATE]}
        label={t('label_discount_percentage')}
        name={CouponFormFields.RATE}
      >
        <InputNumber placeholder={t('placeholder_discount_percentage')} />
      </StyledFormItem>
      <StyledFormItem label={t('label_apply_from_amount')} name={CouponFormFields.MIN_ORDER_AMOUNT}>
        <InputNumber placeholder={t('placeholder_apply_from_amount')} />
      </StyledFormItem>
      <StyledFormItem
        rules={rules[CouponFormFields.POPUP_TITLE]}
        label={t('label_popup_title')}
        name={CouponFormFields.POPUP_TITLE}
      >
        <Input placeholder={t('placeholder_coupon_popup_title')} />
      </StyledFormItem>
      <StyledFormItem
        rules={rules[CouponFormFields.POPUP_DESCRIPTION]}
        label={t('label_popup_description')}
        name={CouponFormFields.POPUP_DESCRIPTION}
      >
        <TextEditorWithHtml rows={5} placeholder={t('placeholder_coupon_popup_description')} />
      </StyledFormItem>
      <StyledFormItem
        rules={rules[CouponFormFields.TEMPLATE]}
        label={t('label_coupon_template')}
        name={CouponFormFields.TEMPLATE}
      >
        <Select options={couponTemplateOptions} maxCount={1} />
      </StyledFormItem>
      <StyledFormItem label={t('label_coupon_preview')}>
        <CouponPreview formInstance={formInstance} />
      </StyledFormItem>
      <StyledRow>
        <Button loading={isLoading} htmlType="submit" type="primary" block size="small">
          {submitText ?? t('create_btn')}
        </Button>
        <Button onClick={onReset} htmlType="button" size="small" block>
          {t('cancel_btn')}
        </Button>
      </StyledRow>
    </Form>
  );
};

export default CouponForm;
